import React, { useState, useEffect } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { database } from '../firebase';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';

const COLORS = ['#00D09C', '#6C63FF', '#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA', '#FCBAD3', '#A8DADC'];

export default function Charts({ users }) {
    const [chartData, setChartData] = useState({
        evolutionData: [],
        distributionData: [],
        topNutritionists: [],
        stats: {
            totalNutritionists: 0,
            totalPatients: 0,
            averagePatients: 0
        }
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!users || Object.keys(users).length === 0) {
            setLoading(false);
            return;
        }

        const loadChartData = async () => {
            try {
                const nutritionists = Object.entries(users)
                    .filter(([id, data]) => data.role === 'nutritionist')
                    .map(([id, data]) => ({ id, ...data }));

                // Buscar pacientes de cada nutricionista
                const patientsPromises = nutritionists.map(async (nutritionist) => {
                    try {
                        // Tentar diferentes estruturas possíveis
                        const paths = [
                            `nutritionists/${nutritionist.id}/patients`,
                            `users/${nutritionist.id}/patients`,
                            `patients/${nutritionist.id}`
                        ];

                        let patientCount = 0;
                        for (const path of paths) {
                            try {
                                const patientsRef = ref(database, path);
                                const snapshot = await get(patientsRef);
                                if (snapshot.exists()) {
                                    const patients = snapshot.val();
                                    patientCount = patients ? Object.keys(patients).length : 0;
                                    break;
                                }
                            } catch (err) {
                                continue;
                            }
                        }

                        return {
                            nutritionistId: nutritionist.id,
                            name: nutritionist.name || nutritionist.email || 'Sem nome',
                            patientCount,
                            createdAt: nutritionist.createdAt || nutritionist.timestamp || Date.now()
                        };
                    } catch (error) {
                        console.error(`Erro ao buscar pacientes de ${nutritionist.id}:`, error);
                        return {
                            nutritionistId: nutritionist.id,
                            name: nutritionist.name || nutritionist.email || 'Sem nome',
                            patientCount: 0,
                            createdAt: nutritionist.createdAt || nutritionist.timestamp || Date.now()
                        };
                    }
                });

                const nutritionistsWithPatients = await Promise.all(patientsPromises);

                // Calcular estatísticas
                const totalPatients = nutritionistsWithPatients.reduce((sum, n) => sum + n.patientCount, 0);
                const averagePatients = nutritionists.length > 0 ? (totalPatients / nutritionists.length).toFixed(1) : 0;

                // Dados de evolução (por mês)
                const evolutionMap = new Map();
                nutritionistsWithPatients.forEach(n => {
                    const date = new Date(n.createdAt);
                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    evolutionMap.set(monthKey, (evolutionMap.get(monthKey) || 0) + 1);
                });

                const evolutionData = Array.from(evolutionMap.entries())
                    .map(([month, count]) => {
                        const [year, monthNum] = month.split('-');
                        return {
                            month: `${monthNum}/${year}`,
                            nutricionistas: count,
                            acumulado: 0
                        };
                    })
                    .sort((a, b) => {
                        const [aMonth, aYear] = a.month.split('/');
                        const [bMonth, bYear] = b.month.split('/');
                        if (aYear !== bYear) return aYear - bYear;
                        return aMonth - bMonth;
                    });

                // Calcular acumulado
                let acumulado = 0;
                evolutionData.forEach(item => {
                    acumulado += item.nutricionistas;
                    item.acumulado = acumulado;
                });

                // Dados de distribuição (pizza)
                const distributionRanges = [
                    { name: '0 pacientes', min: 0, max: 0 },
                    { name: '1-5 pacientes', min: 1, max: 5 },
                    { name: '6-10 pacientes', min: 6, max: 10 },
                    { name: '11-20 pacientes', min: 11, max: 20 },
                    { name: '21+ pacientes', min: 21, max: Infinity }
                ];

                const distributionData = distributionRanges.map(range => {
                    const count = nutritionistsWithPatients.filter(n =>
                        n.patientCount >= range.min && n.patientCount <= range.max
                    ).length;
                    return {
                        name: range.name,
                        value: count
                    };
                }).filter(item => item.value > 0);

                // Top 10 nutricionistas por número de pacientes
                const topNutritionists = nutritionistsWithPatients
                    .sort((a, b) => b.patientCount - a.patientCount)
                    .slice(0, 10)
                    .map(n => ({
                        name: n.name.length > 15 ? n.name.substring(0, 15) + '...' : n.name,
                        pacientes: n.patientCount
                    }));

                setChartData({
                    evolutionData,
                    distributionData,
                    topNutritionists,
                    stats: {
                        totalNutritionists: nutritionists.length,
                        totalPatients,
                        averagePatients: parseFloat(averagePatients)
                    }
                });
            } catch (error) {
                console.error('Erro ao carregar dados dos gráficos:', error);
            } finally {
                setLoading(false);
            }
        };

        loadChartData();
    }, [users]);

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Carregando gráficos...</div>;
    }

    return (
        <div style={{ marginTop: '2rem' }}>
            {/* Cards de Estatísticas */}
            <div className="stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="stat-card">
                    <div className="stat-label">Total de Nutricionistas</div>
                    <div className="stat-value">{chartData.stats.totalNutritionists}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Total de Pacientes</div>
                    <div className="stat-value">{chartData.stats.totalPatients}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-label">Média de Pacientes/Nutri</div>
                    <div className="stat-value">{chartData.stats.averagePatients}</div>
                </div>
            </div>

            {/* Grid de Gráficos */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
            }}>
                {/* Gráfico de Evolução */}
                <div className="stat-card" style={{ minHeight: '300px' }}>
                    <h3 style={{ marginBottom: '1rem', color: 'var(--text-main)', fontSize: '1.2rem' }}>
                        Evolução de Nutricionistas
                    </h3>
                    {chartData.evolutionData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={chartData.evolutionData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis
                                    dataKey="month"
                                    stroke="var(--text-muted)"
                                    style={{ fontSize: '12px' }}
                                />
                                <YAxis stroke="var(--text-muted)" style={{ fontSize: '12px' }} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'var(--bg-card)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '8px',
                                        color: 'var(--text-main)'
                                    }}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="acumulado"
                                    stroke="#00D09C"
                                    strokeWidth={2}
                                    name="Total Acumulado"
                                    dot={{ fill: '#00D09C', r: 4 }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="nutricionistas"
                                    stroke="#6C63FF"
                                    strokeWidth={2}
                                    name="Novos no Mês"
                                    dot={{ fill: '#6C63FF', r: 4 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                            Sem dados suficientes para exibir gráfico
                        </div>
                    )}
                </div>

                {/* Gráfico de Distribuição */}
                <div className="stat-card" style={{ minHeight: '300px' }}>
                    <h3 style={{ marginBottom: '1rem', color: 'var(--text-main)', fontSize: '1.2rem' }}>
                        Distribuição de Pacientes
                    </h3>
                    {chartData.distributionData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={chartData.distributionData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {chartData.distributionData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'var(--bg-card)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '8px',
                                        color: 'var(--text-main)'
                                    }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                            Sem dados suficientes para exibir gráfico
                        </div>
                    )}
                </div>
            </div>

            {/* Gráfico de Barras - Top 10 */}
            {chartData.topNutritionists.length > 0 && (
                <div className="stat-card" style={{ marginBottom: '2rem' }}>
                    <h3 style={{ marginBottom: '1rem', color: 'var(--text-main)', fontSize: '1.2rem' }}>
                        Top 10 Nutricionistas por Número de Pacientes
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={chartData.topNutritionists}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                            <XAxis
                                dataKey="name"
                                stroke="var(--text-muted)"
                                style={{ fontSize: '12px' }}
                                angle={-45}
                                textAnchor="end"
                                height={80}
                            />
                            <YAxis stroke="var(--text-muted)" style={{ fontSize: '12px' }} />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: 'var(--bg-card)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '8px',
                                    color: 'var(--text-main)'
                                }}
                            />
                            <Bar dataKey="pacientes" fill="#00D09C" radius={[8, 8, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}





